import { isFunc } from '../utils/common';
import type { FnMapper, FnResult, ParallelOptions } from './piterate';
import { piterate } from './piterate';

/**
 * Allows async iterating of an iterable; returns results to a second callback.
 * Result order IS guaranteed to match input order.
 *
 * This overload has options after both callbacks
 *
 * @param iterable
 * @param fnMapper
 * @param fnResult
 * @param options
 */
export function pqueue<T, U>(
	iterable: Iterable<T>,
	options: ParallelOptions | undefined,
	fnMapper: FnMapper<T, U>,
	fnResult: FnResult<U>
): Promise<void>;
/**
 * This overload has no options
 * @param iterable
 * @param fnMapper
 * @param fnResult
 */
export function pqueue<T, U>(
	iterable: Iterable<T>,
	fnMapper: FnMapper<T, U>,
	fnResult: FnResult<U>
): Promise<void>;
/**
 * Options after both callbacks
 * @deprecated
 * @param iterable
 * @param fnMapper
 * @param fnResult
 * @param options
 */
export function pqueue<T, U>(
	iterable: Iterable<T>,
	fnMapper: FnMapper<T, U>,
	fnResult: FnResult<U>,
	options: ParallelOptions | undefined
): Promise<void>;
export function pqueue<T, U>(
	iterable: Iterable<T>,
	arg1: ParallelOptions | undefined | FnMapper<T, U>,
	arg2: FnMapper<T, U> | FnResult<U>,
	arg3?: ParallelOptions | FnResult<U>
): Promise<void> {
	// Extra complication for overloads
	let fnMapper: FnMapper<T, U>;
	let fnResult: FnResult<U>;
	let options: ParallelOptions | undefined;

	if (isFunc(arg1)) {
		fnMapper = arg1;
		fnResult = arg2 as FnResult<U>;
		options = arg3 as ParallelOptions | undefined;
	} else {
		options = arg1 as ParallelOptions | undefined;
		fnMapper = arg2 as FnMapper<T, U>;
		fnResult = arg3 as FnResult<U>;
	}

	let nextNeededResultIdx = 0;
	const queuedResults = new Map<number, U>();

	return piterate(iterable, options, fnMapper, (result, index) => {
		if (nextNeededResultIdx === index) {
			// This result is the next needed, so just emit right away
			fnResult(result, index);
			nextNeededResultIdx++;

			// There may be other results queued behind this one, so emit as much as possible
			while (queuedResults.size > 0 && queuedResults.has(nextNeededResultIdx)) {
				fnResult(queuedResults.get(nextNeededResultIdx)!, nextNeededResultIdx);
				queuedResults.delete(nextNeededResultIdx);
				++nextNeededResultIdx;
			}
		} else {
			// We cannot send this result yet...
			queuedResults.set(index, result);
		}
	});
}
